import { useEffect, useState } from 'react';
import { PUSH_TITLE_MAX, PUSH_BODY_MAX } from '@wineryxplorer/shared';
import { trpc } from '../../lib/trpc';
import { PageContainer } from '../../components/PageContainer';
import { Button } from '../../components/ui/Button';
import { CharCounter } from '../../components/ui/CharCounter';
import { Typography } from '../../components/ui/Typography';

type TargetType = '' | 'venue' | 'event';

interface PickedUser {
  id: string;
  label: string;
}

const inputClass =
  'mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-info-500 focus:ring-1 focus:ring-info-500 outline-none';

export const AdminPushTest = () => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [picked, setPicked] = useState<PickedUser | null>(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [targetType, setTargetType] = useState<TargetType>('');
  const [targetId, setTargetId] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const users = trpc.adminUsers.list.useQuery(
    { search: debouncedSearch, limit: 8, offset: 0 },
    { enabled: debouncedSearch.length > 0 && !picked }
  );

  const send = trpc.push.adminTestSend.useMutation();

  const trimmedTitle = title.trim();
  const trimmedBody = body.trim();
  const titleOk = trimmedTitle.length > 0 && title.length <= PUSH_TITLE_MAX;
  const bodyOk = trimmedBody.length > 0 && body.length <= PUSH_BODY_MAX;
  const targetOk = targetType === '' || targetId.trim().length > 0;
  const canSend = picked !== null && titleOk && bodyOk && targetOk && !send.isPending;

  const handleSend = () => {
    if (!picked || !canSend) return;
    send.mutate({
      userId: picked.id,
      title: trimmedTitle,
      body: trimmedBody,
      ...(targetType ? { target: { type: targetType, id: targetId.trim() } } : {}),
    });
  };

  return (
    <PageContainer>
      <div className="space-y-6 max-w-xl">
        <div className="flex items-start justify-between gap-4">
          <Typography variant="heading1" as="h1">
            Push test
          </Typography>
          <div className="flex flex-col items-end gap-1">
            <Button loading={send.isPending} disabled={!canSend} onClick={handleSend}>
              Send
            </Button>
            {send.error && (
              <p role="alert" className="text-sm text-error">
                {send.error.message}
              </p>
            )}
            {send.data && (
              <div className="text-sm text-gray-700 text-right">
                <p>Sent to {send.data.deviceCount} device(s)</p>
                {send.data.deviceCount === 0 && <p>This user has no registered devices.</p>}
              </div>
            )}
          </div>
        </div>

        <div>
          {picked ? (
            <div className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
              <span>{picked.label}</span>
              <Button size="sm" variant="ghost" onClick={() => setPicked(null)}>
                Change
              </Button>
            </div>
          ) : (
            <>
              <label htmlFor="push-user-search" className="block text-sm font-medium text-gray-900">
                Find a user
              </label>
              <input
                id="push-user-search"
                type="search"
                value={search}
                placeholder="Name, username or email"
                onChange={(e) => setSearch(e.target.value)}
                className={inputClass}
              />
              {!!users.data?.items.length && (
                <ul className="mt-2 rounded-md border border-gray-200 divide-y divide-gray-100">
                  {users.data.items.map((u) => (
                    <li key={u._id}>
                      <button
                        type="button"
                        className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                        onClick={() => setPicked({ id: u._id, label: u.email })}
                      >
                        {u.name} ({u.email})
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {users.data && users.data.items.length === 0 && (
                <p className="mt-2 text-sm text-gray-500">No users found.</p>
              )}
            </>
          )}
        </div>

        <div>
          <div className="flex items-baseline justify-between">
            <label htmlFor="push-title" className="block text-sm font-medium text-gray-900">
              Title
            </label>
            <CharCounter length={title.length} max={PUSH_TITLE_MAX} />
          </div>
          <input
            id="push-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className={inputClass}
          />
        </div>

        <div>
          <div className="flex items-baseline justify-between">
            <label htmlFor="push-body" className="block text-sm font-medium text-gray-900">
              Body
            </label>
            <CharCounter length={body.length} max={PUSH_BODY_MAX} />
          </div>
          <textarea
            id="push-body"
            rows={3}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className={inputClass}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="push-target" className="block text-sm font-medium text-gray-900">
              Target
            </label>
            <select
              id="push-target"
              value={targetType}
              onChange={(e) => setTargetType(e.target.value as TargetType)}
              className={inputClass}
            >
              <option value="">None</option>
              <option value="venue">Venue</option>
              <option value="event">Event</option>
            </select>
          </div>
          {targetType && (
            <div>
              <label htmlFor="push-target-id" className="block text-sm font-medium text-gray-900">
                Target id
              </label>
              <input
                id="push-target-id"
                type="text"
                value={targetId}
                onChange={(e) => setTargetId(e.target.value)}
                className={inputClass}
              />
            </div>
          )}
        </div>
      </div>
    </PageContainer>
  );
};
